import React, { useEffect, useState } from 'react';
import { MapPin, Users, Activity, Package, Loader2, CheckCircle, XCircle, ArrowRight, Plus } from 'lucide-react';
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import operatorKitService from '@/api/services/operatorKitService';
import type { DashboardStats, RecentUser } from '@/api/services/operatorKitService';
import { useTranslation } from "react-i18next";
import { getErrorMessage } from "@/utils/errorUtils";

const OperatorStatsPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [users, setUsers] = useState<RecentUser[]>([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [dashboardData, recentUsers] = await Promise.all([
        operatorKitService.getDashboard(),
        operatorKitService.getRecentUsers(),
      ]);
      setStats(dashboardData);
      setUsers(recentUsers);
    } catch (error: unknown) {
      console.error('Dashboard fetch error:', error);
      showMessage(
        'error',
        getErrorMessage(error) ||
          t('operator.statsPage.messages.loadFailed', { defaultValue: 'Failed to load dashboard data' })
      );
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'inactive':
        return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  const formatJoinedDate = (raw: string | Date | null | undefined): string => {
    if (!raw) return t('operator.statsPage.common.na', { defaultValue: 'N/A' });
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) {
      return t('operator.statsPage.common.na', { defaultValue: 'N/A' });
    }
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#2E5A88]" />
      </div>
    );
  }

  const statsData = [
    {
      icon: Users,
      label: t('operator.statsPage.stats.totalUsers', { defaultValue: 'Total Users' }),
      value: stats?.totalUsers?.toLocaleString() || '0',
      tone: 'navy',
    },
    {
      icon: Package,
      label: t('operator.statsPage.stats.totalKits', { defaultValue: 'Total Kits' }),
      value: stats?.totalKits?.toLocaleString() || '0',
      sublabel: (stats?.pendingKits ? `${stats.pendingKits} ${t('operator.statsPage.common.pendingLower', { defaultValue: 'pending' })}` : ''),
      tone: 'navy',
    },
    {
      icon: CheckCircle,
      label: t('operator.statsPage.stats.activeKits', { defaultValue: 'Active Kits' }),
      value: stats?.activeKits?.toLocaleString() || '0',
      tone: 'navy',
    },
    {
      icon: Activity,
      label: t('operator.statsPage.stats.networkStatus', { defaultValue: 'Network Status' }),
      // Network status / uptime is a computed field on the Kit↔Hotspot link (KX-34,
      // numeric uptime_pct replacing the String '0%'). Show the backend value only
      // when present; never a hardcoded '0%' placeholder (KX-110 item 5, AGENTS §2.2).
      value: stats?.networkStatus || t('operator.statsPage.pending.allocation', { defaultValue: 'Pending allocation' }),
      tone: 'green',
    },
    {
      icon: MapPin,
      label: t('operator.statsPage.stats.locations', { defaultValue: 'Locations' }),
      value: stats?.locations?.toString() || '0',
      tone: 'navy',
    },
  ];

  return (
    <>
      <Helmet>
        <title>{t('operator.statsPage.meta.title', { defaultValue: 'Operator Dashboard | KonectX' })}</title>
        <meta
          name="description"
          content={t('operator.statsPage.meta.description', {
            defaultValue:
              'Overview of your KonectX operator activity — total users, active kits, network status, and recent connections.',
          })}
        />
      </Helmet>

      <div className="p-4 sm:p-7 max-w-7xl mx-auto">
        {/* Message */}
        {message && (
          <div
            className={`mb-6 p-4 rounded-lg flex items-center gap-2 border ${
              message.type === 'success'
                ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800'
                : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800'
            }`}
          >
            {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
            <span className="text-sm font-medium">{message.text}</span>
          </div>
        )}

        {/* Header with primary CTA */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
              {t('operator.statsPage.title', { defaultValue: 'Dashboard' })}
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t('operator.statsPage.subtitle', {
                defaultValue: 'Your network activity at a glance.',
              })}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => navigate('/operator/AllKits')}
              className="px-5 py-2.5 rounded-md font-semibold text-sm border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
            >
              <Package className="w-4 h-4" />
              {t('operator.statsPage.actions.viewKits', { defaultValue: 'My Kits' })}
            </button>
            <button
              onClick={() => navigate('/operator/registerKit')}
              className="px-5 py-2.5 rounded-md font-semibold text-sm bg-[#2E5A88] hover:bg-[#244a73] text-white transition-colors flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" />
              {t('operator.statsPage.actions.registerKit', { defaultValue: 'Register New Kit' })}
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
          {statsData.map((stat, index) => (
            <div
              key={index}
              className="bg-white dark:bg-[#1a1a1a] rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-800"
            >
              <div className="flex items-start justify-between mb-4">
                <div
                  className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    stat.tone === 'green'
                      ? 'bg-green-50 dark:bg-green-900/20'
                      : 'bg-[#2E5A88]/10 dark:bg-[#2E5A88]/20'
                  }`}
                >
                  <stat.icon
                    className={`w-6 h-6 ${stat.tone === 'green' ? 'text-green-600' : 'text-[#2E5A88]'}`}
                  />
                </div>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-1">{stat.value}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Recent Users + Side Info */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Users (takes 2 cols) */}
          <div className="lg:col-span-2 bg-white dark:bg-[#1a1a1a] rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('operator.statsPage.recentUsers', { defaultValue: 'Recent Users' })}
              </h3>
              {users.length > 0 && (
                <button
                  onClick={() => navigate('/operator/MyHotspots')}
                  className="text-sm font-semibold text-[#2E5A88] hover:underline flex items-center gap-1"
                >
                  {t('operator.statsPage.actions.viewAllUsers', { defaultValue: 'View all' })}
                  <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="space-y-4">
              {users.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-gray-300 dark:text-gray-700 mx-auto mb-3" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t('operator.statsPage.noRecentUsers', {
                      defaultValue: 'No recent users yet. Users will appear here once your kit is live and they connect.',
                    })}
                  </p>
                </div>
              ) : (
                users.map((user, index) => (
                  <div
                    key={`${user.email}-${index}`}
                    className="flex items-start gap-3 pb-4 border-b last:border-b-0 last:pb-0 border-gray-200 dark:border-gray-800"
                  >
                    <div className="w-10 h-10 rounded-full bg-[#2E5A88]/10 flex items-center justify-center font-semibold text-[#2E5A88] text-sm flex-shrink-0">
                      {user.name
                        .split(' ')
                        .map((n) => n[0])
                        .join('')
                        .toUpperCase()
                        .slice(0, 2)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-gray-900 dark:text-white truncate">
                            {user.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${getStatusColor(user.status)}`}>
                          {t(`operator.statsPage.userStatus.${user.status}`, {
                            defaultValue: user.status.charAt(0).toUpperCase() + user.status.slice(1),
                          })}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {user.location || t('operator.statsPage.common.unknown', { defaultValue: 'Unknown' })}
                        </span>
                        <span>•</span>
                        <span>{formatJoinedDate(user.joinedDate)}</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Side Info Card */}
          <div className="lg:col-span-1 bg-gradient-to-br from-[#2E5A88] to-[#1e3d5d] rounded-xl p-6 text-white">
            <h3 className="text-lg font-bold mb-2">
              {t('operator.statsPage.sideCard.title', { defaultValue: 'Grow Your Network' })}
            </h3>
            <p className="text-sm text-white/80 mb-5 leading-relaxed">
              {t('operator.statsPage.sideCard.body', {
                defaultValue:
                  'Register additional hotspot kits to expand your coverage area and accrue more KXT rewards.',
              })}
            </p>
            <button
              onClick={() => navigate('/operator/registerKit')}
              className="w-full bg-white text-[#2E5A88] hover:bg-gray-100 transition-colors font-semibold text-sm py-2.5 rounded-md flex items-center justify-center gap-2"
            >
              {t('operator.statsPage.sideCard.cta', { defaultValue: 'Register Another Kit' })}
              <ArrowRight className="w-4 h-4" />
            </button>

            <div className="mt-6 pt-6 border-t border-white/20 text-xs text-white/70">
              <p>
                {t('operator.statsPage.sideCard.hint', {
                  defaultValue: 'Each kit goes through 3 steps: device info, KYC, and payout setup.',
                })}
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OperatorStatsPage;
